'use client';

import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { BackendAuth } from '@/lib/auth-backend';
import { useRouter } from 'next/navigation';
import { useXoffiOsRegistration } from '@/hooks/useXoffiOsRegistration';

/**
 * NotificationProvider - Provides real-time notifications via SSE
 *
 * Connects to /api/notifications/stream and displays toast notifications
 * for events like invitation.accepted, ui.toast, etc.
 *
 * Features:
 * - Automatic reconnection on disconnect
 * - Authentication via JWT token
 * - Event-specific toast handling
 * - Action buttons in toasts (e.g., navigate to URL)
 */

const NotificationContext = createContext({
  isConnected: false,
  reconnect: () => {},
});

export function useNotifications() {
  return useContext(NotificationContext);
}

export function NotificationProvider({ children }) {
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const router = useRouter();

  // v0.6.6: Register the XOFFI-OS Desktop client as a pseudo push_tokens
  // entry so it appears in the push-tests UI. The hook is a no-op for
  // regular browser sessions (only acts when running inside XOFFI-OS Electron).
  useXoffiOsRegistration();

  // Handle incoming toast events
  const handleToastEvent = useCallback((data) => {
    const { type, title, description, duration = 5000, action } = data;

    const toastOptions = {
      description,
      duration,
    };

    // Add action button if provided
    if (action?.label && action?.url) {
      toastOptions.action = {
        label: action.label,
        onClick: () => router.push(action.url),
      };
    }

    // Display appropriate toast type
    switch (type) {
      case 'success':
        toast.success(title, toastOptions);
        break;
      case 'error':
        toast.error(title, toastOptions);
        break;
      case 'warning':
        toast.warning(title, toastOptions);
        break;
      case 'info':
      default:
        toast.info(title, toastOptions);
        break;
    }
  }, [router]);

  // Handle invitation accepted event
  const handleInvitationAccepted = useCallback((data) => {
    // Use success toast with celebration
    toast.success(data.title || 'Einladung angenommen! 🎉', {
      description: data.description,
      duration: data.duration || 8000,
      action: data.action ? {
        label: data.action.label,
        onClick: () => router.push(data.action.url),
      } : undefined,
    });
  }, [router]);

  // Connect to SSE endpoint
  const connect = useCallback(async () => {
    // Check if user is authenticated
    if (!BackendAuth.isAuthenticated()) {
      return;
    }

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      // Get auth token for the request
      const token = BackendAuth.getToken();
      if (!token) {
        return;
      }

      // EventSource doesn't support custom headers, so we pass token via query param
      // The backend SSE endpoint accepts ?token=xxx for authentication
      const url = `/api/notifications/stream?token=${encodeURIComponent(token)}`;

      // Create EventSource
      const eventSource = new EventSource(url);

      eventSource.onopen = () => {
        setIsConnected(true);

        // Clear any pending reconnect
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
      };

      // Handle connection event
      eventSource.addEventListener('connected', () => {});

      // Handle toast events
      eventSource.addEventListener('ui.toast', (event) => {
        try {
          const data = JSON.parse(event.data);
          handleToastEvent(data);
        } catch (e) {
          console.error('SSE: Failed to parse toast event', e);
        }
      });

      // Handle invitation accepted events
      eventSource.addEventListener('invitation.accepted', (event) => {
        try {
          const data = JSON.parse(event.data);
          handleInvitationAccepted(data);
        } catch (e) {
          console.error('SSE: Failed to parse invitation event', e);
        }
      });

      // Handle generic notifications
      eventSource.addEventListener('notification', (event) => {
        try {
          const data = JSON.parse(event.data);
          handleToastEvent(data);
        } catch (e) {
          console.error('SSE: Failed to parse notification event', e);
        }
      });

      // Handle push events (parallel to native FCM/Expo, also fires the
      // xoffi-desktop-push DOM event for Electron native-notification bridge
      // in XOFFI-OS BG-Window where Chromium PushManager.subscribe() fails).
      eventSource.addEventListener('push', (event) => {
        try {
          const data = JSON.parse(event.data);
          const title = data?.title || 'XOFFI';
          const body = data?.body || '';
          // Dispatch DOM CustomEvent for Electron preload bridge (Phase 2)
          try {
            window.dispatchEvent(new CustomEvent('xoffi-desktop-push', {
              detail: { title, body, data: data?.data || {}, channel_id: data?.channel_id },
            }));
          } catch {}
          // Visible in-page toast (browser tabs, also visible to XOFFI-OS BG if open)
          toast(title, { description: body, duration: 6000 });
        } catch (e) {
          console.error('SSE: Failed to parse push event', e);
        }
      });

      // Handle calendar reminder events
      eventSource.addEventListener('calendar.reminder', (event) => {
        try {
          const data = JSON.parse(event.data);
          const eventId = data.event_id;
          const minutes = data.starts_in_minutes ?? data.minutes_before;
          const description = data.description
            || (minutes != null ? `In ${minutes} Min` : '')
              + (data.location ? ` · ${data.location}` : '');
          toast.info(`⏰ ${data.title || 'Termin'}`, {
            description,
            duration: 10000,
            action: eventId ? {
              label: 'Öffnen',
              onClick: () => router.push(`/calendar?event=${eventId}`),
            } : undefined,
          });
        } catch (e) {
          console.error('SSE: Failed to parse calendar.reminder event', e);
        }
      });

      // Handle errors
      eventSource.onerror = (error) => {
        console.warn('SSE: Connection error', error);
        setIsConnected(false);
        eventSource.close();

        // Schedule reconnect after 5 seconds
        if (!reconnectTimeoutRef.current) {
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            connect();
          }, 5000);
        }
      };

      eventSourceRef.current = eventSource;

    } catch (error) {
      console.error('SSE: Failed to connect', error);
      setIsConnected(false);
    }
  }, [handleToastEvent, handleInvitationAccepted]);

  // Reconnect function exposed via context
  const reconnect = useCallback(() => {
    connect();
  }, [connect]);

  // Connect on mount and when auth changes
  useEffect(() => {
    // Delay initial connection to allow auth to settle
    const initTimeout = setTimeout(() => {
      connect();
    }, 1000);

    // Listen for auth changes
    const handleStorageChange = (e) => {
      if (e.key === 'auth_token') {
        if (e.newValue) {
          // User logged in
          connect();
        } else {
          // User logged out
          if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
          }
          setIsConnected(false);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearTimeout(initTimeout);
      window.removeEventListener('storage', handleStorageChange);

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [connect]);

  return (
    <NotificationContext.Provider value={{ isConnected, reconnect }}>
      {children}
    </NotificationContext.Provider>
  );
}
